// todo use error translations
import axios, { AxiosError, AxiosResponse } from "axios";
import { v4 as uuidv4 } from "uuid";

import { DTOModel } from "../../models/base/dto-model";
import { STOModel } from "../../models/base/sto-model";

import { Sort } from "../../models/api/sort";
import { Page } from "../../models/api/page";
import { Request } from "../../models/api/request";
import { LoadRequest, LoadResponse } from "../../models/api/load";
import { SaveRequest, SaveResponse } from "../../models/api/save";
import { ListSortDirection } from "../../models/api/sort";
import { PropsFilter } from "../../models/api/props-filter";

import { getAuthToken } from "../../custom-hooks/useDecodedToken/useDecodedToken";
import { AVAILABLE_LANGUAGES } from "src/utils/constants/constants";
import { AvailableLanguageRef } from "src/models/available-language";
import { CompanyAccountRef } from "src/models/company-accounts/company-accounts";

export interface ApiPaths {
  servicePath: string;
  loadItemsPath: string;
  saveItemsPath?: string;
}
export class BaseApiService<TDTOModel extends DTOModel, TSTOModel extends STOModel> {
  protected apiURL = `${import.meta.env.VITE_API_URL}`;
  public defaultSort: Sort[] = [{ name: "createdAt", direction: ListSortDirection.Descending } as Sort];

  defaultPage = { pageNumber: 0, pageSize: 10 } as Page;
  singleItemPage = { pageNumber: 0, pageSize: 1 } as Page;

  defaultPropsFilter = {
    ids: [],
    createdByUsers: [],
    modifiedByUsers: [],
    isDeleted: false,
  } as PropsFilter;

  defaultTimeout = 60;

  protected noAuthToken = false;

  currentCompanyAccountId: string | null = localStorage.getItem("currentCompanyAccountId") ?? null; // as we pass a Ref if this is NOT null, missing ID should be initialized with null

  constructor(protected apiPaths: ApiPaths) {}

  public withNoAuth() {
    this.noAuthToken = true;
  }

  public withAuth() {
    this.noAuthToken = false;
  }

  public getApiUrl() {
    return this.apiURL;
  }

  public useLoadItemsPath(loadItemsPath: string) {
    if (loadItemsPath) {
      this.apiPaths.loadItemsPath = loadItemsPath;
    }
  }

  public useSaveItemsPath(saveItemsPath: string) {
    if (saveItemsPath) {
      this.apiPaths.saveItemsPath = saveItemsPath;
    }
  }

  /**
   * Read items derived from DTOModel
   * @param url
   * @param ids
   * @param sorts
   * @param page
   * @param timeout
   * @param source
   */
  public async loadItems(
    propsFilter: PropsFilter = this.defaultPropsFilter,
    searchFilter: string = "",
    sorts: Sort[] = this.defaultSort,
    page: Page = this.defaultPage,
    exactMatch: boolean = false,
    timeout: number = this.defaultTimeout,
    signal?: AbortSignal,
    withoutCompanyAccount?: boolean
  ): Promise<LoadResponse<TDTOModel>> {
    const request = this.createLoadRequest(
      propsFilter,
      searchFilter,
      sorts,
      page,
      exactMatch,
      timeout,
      withoutCompanyAccount
    );

    const token = getAuthToken();
    const url = `${this.apiURL}${this.apiPaths.servicePath}${this.apiPaths.loadItemsPath}`;
    let response = {
      data: {
        items: [] as TDTOModel[],
        totalItems: 0,
      } as LoadResponse<TDTOModel>,
    };

    try {
      response = await axios.post<LoadResponse<TDTOModel>>(url, request, {
        headers: { Authorization: this.noAuthToken ? "" : `Bearer ${token}` },
        signal,
      });
    } catch (err) {
      const error = err as AxiosError;
      let errorMessage = error?.response?.data?.error ?? error?.message ?? error;

      console.log((error?.response?.status ?? "") + ": Failure loading data. ", errorMessage);

      switch (error?.response?.status) {
        case 400: {
          errorMessage = "Bad request/wrong data sent.";
          break;
        }
        case 401: {
          errorMessage = "Not authenticated. Try to sign in again.";
          break;
        }
        case 403: {
          errorMessage = "Not authorized. Review your access rights.";
          break;
        }
        case 404: {
          errorMessage = "Data not found.";
          break;
        }

        case 500: {
          errorMessage = "Server error.";
          break;
        }

        case 502: {
          errorMessage = "Bad gateway / endpoint unreachable.";
          break;
        }

        case 503: {
          errorMessage = "Service unavailable.";
          break;
        }

        default:
          break;
      }

      response.data.error = errorMessage;
    }

    return response.data as LoadResponse<TDTOModel>;
  }

  /**
   * Create/Update/Delete items derived from STOModel
   * @param url
   * @param items
   * @param timeout
   */
  public async saveItems(
    items: TSTOModel[],
    timeout = this.defaultTimeout,
    correlationId?: string | null
  ): Promise<SaveResponse> {
    const request = correlationId
      ? this.createSaveRequest(items, timeout, correlationId)
      : this.createSaveRequest(items, timeout);
    const token = getAuthToken();
    const url = `${this.apiURL}${this.apiPaths.servicePath}${this.apiPaths.saveItemsPath}`;

    let response = {
      data: { isSucceeded: false } as SaveResponse,
    };
    try {
      response = await axios.post<SaveResponse>(url, request, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      const error = err as AxiosError;
      const errorMessage = error?.response?.data?.error ? error?.response?.data?.error : error?.message ?? error;

      console.log((error?.response?.status ?? "") + ": Failure saving data. ", errorMessage);

      if (response.data) {
        response.data.error = errorMessage;
      }
    }

    return response?.data;
  }

  public async delete(url: string, id: string, timeout = this.defaultTimeout): Promise<SaveResponse> {
    const item = { id, isDeleted: true } as TSTOModel;

    return await this.saveItems([item], timeout);
  }

  /**Get file content
   * @param path
   * @param propsFilter
   * @param signal
   *
   */
  public async getFileContent(path: string, propsFilter?: PropsFilter, signal?: AbortSignal): Promise<Blob> {
    const request = this.createLoadRequest(
      {
        ...propsFilter,
      },
      "",
      undefined,
      undefined,
      undefined,
      60
    );
    const token = getAuthToken();
    const url = `${this.apiURL}${this.apiPaths.servicePath}/${path}`;

    let response: AxiosResponse<Blob, any> = {
      data: new Blob(),
      status: 200,
      statusText: "OK",
      headers: {},
      config: {},
    };
    let errorMessage = "";
    try {
      response = await axios.post<Blob>(url, request, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
        signal,
      });
    } catch (err) {
      const error = err as AxiosError;
      errorMessage = error?.response?.data?.error ? error?.response?.data?.error : error?.message ?? error;

      console.log((error?.response?.status ?? "") + ": Failure loading file. ", errorMessage);

      switch (error?.response?.status) {
        case 400: {
          errorMessage = "Bad request/wrong data sent.";
          break;
        }
        case 401: {
          errorMessage = "Not authenticated. Try to sign in again.";
          break;
        }
        case 403: {
          errorMessage = "Not authorized. Review your access rights.";
          break;
        }
        case 404: {
          errorMessage = "Data not found.";
          break;
        }

        case 500: {
          errorMessage = "Failure loading file - Server error.";
          break;
        }

        case 502: {
          errorMessage = "Bad gateway / endpoint unreachable.";
          break;
        }

        case 503: {
          errorMessage = "Service unavailable.";
          break;
        }

        default:
          break;
      }
    }

    const { data } = response;
    return data;
  }

  protected createLoadRequest(
    propsFilter: PropsFilter = this.defaultPropsFilter,
    searchFilter: string,
    sorts: Sort[] = this.defaultSort,
    page: Page = this.defaultPage,
    exactMatch: boolean = false,
    timeout = this.defaultTimeout,
    withoutCompanyAccount?: boolean
  ): LoadRequest {
    const baseRequest = this.createRequest(timeout, undefined, withoutCompanyAccount);
    if (!page) page = this.singleItemPage;

    return {
      ...baseRequest,
      searchFilter,
      propsFilter,
      sorts,
      page,
      exactMatch,
    } as LoadRequest;
  }

  protected createSaveRequest(
    items: TSTOModel[],
    timeout = this.defaultTimeout,
    correlationId?: string
  ): SaveRequest<TSTOModel> {
    const baseRequest = this.createRequest(timeout, correlationId);

    return {
      ...baseRequest,
      items: items,
    } as SaveRequest<TSTOModel>;
  }

  protected createRequest(timeout = this.defaultTimeout, correlationId?: string, withoutCompanyAccount?: boolean) {
    const currentLanguageCode = localStorage.getItem("i18nextLng");
    const currentLanguage = Object.values(AVAILABLE_LANGUAGES).find(
      (language) => language.code === currentLanguageCode
    );
    return {
      id: uuidv4(),
      invokerId: uuidv4(),
      correlationId: correlationId ?? uuidv4(),
      timeout,
      currentCompanyAccount: this.currentCompanyAccountId
        ? !withoutCompanyAccount
          ? {
              id: this.currentCompanyAccountId,
            }
          : null
        : null,
      usedLanguageId: currentLanguage?.id ?? null,
    } as Request;
  }
}
